import { Controller, useFormContext } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { useEffect } from 'react';
import { SxProps } from '@mui/system';
import useIsFirstRender from 'hooks/useIsFirstRender';
import { emailPattern, patternRule } from './helpers';

interface FormInputTextProps {
    name: string;
    label?: string;
    required?: boolean;
    maxLength?: number;
    minLength?: number;
    fullWidth?: boolean;
    helperText?: string;
    margin?: 'none' | 'normal' | 'dense' | undefined;
    multiline?: boolean;
    maxRows?: number;
    type?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    sx?: SxProps | undefined;
    regex?: RegExp;
    regexErrorMessage?: string;
}

export const FormInputText = ({
    name,
    label,
    required,
    maxLength = 150,
    minLength = 0,
    fullWidth,
    helperText = 'Field is required',
    margin,
    multiline,
    maxRows,
    type = 'text',
    disabled,
    sx,
    size = 'medium',
    regex,
    regexErrorMessage,
}: FormInputTextProps) => {
    const { control, watch, trigger } = useFormContext();
    const isFirstRender = useIsFirstRender();
    const value = watch(name);

    useEffect(() => {
        if (!isFirstRender) {
            trigger(name);
        }
    }, [value]);

    return (
        <Controller
            rules={{
                minLength: {
                    value: minLength,
                    message: `Min length is ${minLength}`,
                },
                maxLength: {
                    value: maxLength,
                    message: `Max length is ${maxLength}`,
                },
                required: {
                    value: required ? required : false,
                    message: helperText ? helperText : 'This field is required',
                },
                ...(type === 'email' && maxLength ? patternRule(emailPattern, 'Invalid email address') : {}),
                ...(regex
                    ? {
                          pattern: {
                              value: regex,
                              message:
                                  regexErrorMessage || `Invalid format. Please follow the pattern: ${regex.source}`,
                          },
                      }
                    : {}),
            }}
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                    helperText={error ? (error.message ? error.message : helperText) : null}
                    size={size}
                    error={!!error}
                    onChange={e =>
                        onChange(type === 'number' ? (e.target.value === '' ? '' : +e.target.value) : e.target.value)
                    }
                    type={type}
                    value={value ?? ''}
                    fullWidth={fullWidth}
                    label={label}
                    required={required}
                    variant='outlined'
                    margin={margin}
                    multiline={multiline}
                    maxRows={maxRows}
                    disabled={disabled}
                    sx={sx}
                />
            )}
        />
    );
};
